import React, { useState, useEffect, useCallback } from 'react';
import { facturacionService } from '../../services/facturacion.service';
import { Users, Plus, Search, Edit, FileText, Building2, Mail, Phone, Calendar, Loader2, X } from 'lucide-react';

const BillingClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    rfc: '',
    razonSocial: '',
    regimenFiscal: '',
    usoCFDI: '',
    direccion: {
      calle: '',
      numeroExterior: '',
      numeroInterior: '',
      colonia: '',
      municipio: '',
      estado: '',
      codigoPostal: '',
      pais: 'México'
    },
    contacto: {
      email: '',
      telefono: ''
    }
  });

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await facturacionService.clientes.getAll({ search: searchTerm });
      setClients(response.data.data);
    } catch (error) {
      console.error('Error cargando clientes:', error);
      setError('Error al cargar los clientes de facturación');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchTerm !== '') {
        loadClients();
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, loadClients]);

  const filteredClients = clients.filter(client =>
    client.rfc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.razonSocial.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleNewClient = () => {
    setSelectedClient(null);
    setFormData({
      rfc: '',
      razonSocial: '',
      regimenFiscal: '',
      usoCFDI: '',
      direccion: {
        calle: '',
        numeroExterior: '',
        numeroInterior: '',
        colonia: '',
        municipio: '',
        estado: '',
        codigoPostal: '',
        pais: 'México'
      },
      contacto: {
        email: '',
        telefono: ''
      }
    });
    setShowModal(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setFormData({
      rfc: client.rfc,
      razonSocial: client.razonSocial,
      regimenFiscal: client.regimenFiscal,
      usoCFDI: client.usoCFDI,
      direccion: client.direccion || {
        calle: '',
        numeroExterior: '',
        numeroInterior: '',
        colonia: '',
        municipio: '',
        estado: '',
        codigoPostal: '',
        pais: 'México'
      },
      contacto: client.contacto || {
        email: '',
        telefono: ''
      }
    });
    setShowModal(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (selectedClient) {
        // Actualizar cliente existente
        const response = await facturacionService.clientes.update(selectedClient.id, formData);
        setClients(clients.map(c =>
          c.id === selectedClient.id ? response.data : c
        ));
      } else {
        // Crear nuevo cliente
        const response = await facturacionService.clientes.create(formData);
        setClients([...clients, response.data]);
      }

      setShowModal(false);
      // Recargar la lista para obtener datos actualizados
      loadClients();
    } catch (error) {
      console.error('Error guardando cliente:', error);
      setError(error.response?.data?.message || 'Error al guardar el cliente');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = facturacionService.utils.formatCurrency;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 lg:p-8">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">Cargando clientes de facturación...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 lg:p-8">
      {/* Header principal con gradiente */}
      <div className="mb-8">
        <div className="card p-8 bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 text-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full transform translate-x-32 -translate-y-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-400 rounded-full transform -translate-x-24 translate-y-24"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold">Clientes de Facturación</h1>
                <p className="text-white/80 text-lg">Gestión de clientes con datos fiscales para emisión de facturas</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6">
          <div className="card p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
            <div className="flex items-center">
              <div className="w-6 h-6 text-red-500 mr-3">
                ⚠️
              </div>
              <p className="text-red-700 dark:text-red-400 font-medium">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-8">
        <div className="card p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por RFC o Razón Social..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            
            {/* New Client Button */}
            <button 
              className="btn-primary hover-lift flex items-center justify-center space-x-2"
              onClick={handleNewClient}
            >
              <Plus className="w-5 h-5" />
              <span>Nuevo Cliente</span>
            </button>
          </div>
        </div>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredClients.length === 0 ? (
          <div className="col-span-full">
            <div className="card p-12 text-center">
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No hay clientes</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {searchTerm
                  ? 'No se encontraron clientes que coincidan con tu búsqueda.'
                  : 'Aún no has agregado clientes de facturación.'}
              </p>
              {!searchTerm && (
                <button 
                  className="btn-primary hover-lift flex items-center justify-center space-x-2 mx-auto"
                  onClick={handleNewClient}
                >
                  <Plus className="w-5 h-5" />
                  <span>Agregar Primer Cliente</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredClients.map(client => (
            <div key={client.id} className="card-hover p-6 group">
              {/* Client Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {client.razonSocial}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-mono text-gray-600 dark:text-gray-400">{client.rfc}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  client.activo 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {client.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              {/* Client Details */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Régimen Fiscal:</span>
                  <span className="text-gray-900 dark:text-white font-medium">{client.regimenFiscal}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Uso CFDI:</span>
                  <span className="text-gray-900 dark:text-white font-medium">{client.usoCFDI}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Total Facturado:</span>
                  <span className="text-green-600 dark:text-green-400 font-bold">{formatCurrency(client.totalFacturado)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Registro:
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {new Date(client.fechaCreacion).toLocaleDateString('es-MX')}
                  </span>
                </div>
              </div>

              {/* Client Actions */}
              <div className="flex space-x-3">
                <button
                  className="flex-1 btn-secondary hover-lift flex items-center justify-center space-x-2"
                  onClick={() => handleEditClient(client)}
                >
                  <Edit className="w-4 h-4" />
                  <span>Editar</span>
                </button>
                <button className="flex-1 btn-outline hover-lift flex items-center justify-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Facturas</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal para crear/editar cliente */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedClient ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleSaveClient} className="space-y-8">
                {/* Información Fiscal */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Información Fiscal</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">RFC *</label>
                      <input
                        type="text"
                        value={formData.rfc}
                        onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                        maxLength="13"
                        placeholder="Ej: ABC123456789"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Razón Social *</label>
                      <input
                        type="text"
                        value={formData.razonSocial}
                        onChange={(e) => setFormData({ ...formData, razonSocial: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                        placeholder="Nombre de la empresa"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Régimen Fiscal *</label>
                      <select
                        value={formData.regimenFiscal}
                        onChange={(e) => setFormData({ ...formData, regimenFiscal: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="">Seleccionar...</option>
                        <option value="601">601 - General de Ley Personas Morales</option>
                        <option value="603">603 - Personas Morales con Fines no Lucrativos</option>
                        <option value="605">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
                        <option value="606">606 - Arrendamiento</option>
                        <option value="608">608 - Demás ingresos</option>
                        <option value="610">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</option>
                        <option value="611">611 - Ingresos por Dividendos (socios y accionistas)</option>
                        <option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                        <option value="614">614 - Ingresos por intereses</option>
                        <option value="616">616 - Sin obligaciones fiscales</option>
                        <option value="620">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</option>
                        <option value="621">621 - Incorporación Fiscal</option>
                        <option value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                        <option value="623">623 - Opcional para Grupos de Sociedades</option>
                        <option value="624">624 - Coordinados</option>
                        <option value="628">628 - Hidrocarburos</option>
                        <option value="629">629 - De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales</option>
                        <option value="630">630 - Enajenación de acciones en bolsa de valores</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Uso CFDI *</label>
                      <select
                        value={formData.usoCFDI}
                        onChange={(e) => setFormData({ ...formData, usoCFDI: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="">Seleccionar...</option>
                        <option value="G01">G01 - Adquisición de mercancías</option>
                        <option value="G02">G02 - Devoluciones, descuentos o bonificaciones</option>
                        <option value="G03">G03 - Gastos en general</option>
                        <option value="I01">I01 - Construcciones</option>
                        <option value="I02">I02 - Mobilario y equipo de oficina por inversiones</option>
                        <option value="I03">I03 - Equipo de transporte</option>
                        <option value="I04">I04 - Equipo de computo y accesorios</option>
                        <option value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</option>
                        <option value="I06">I06 - Comunicaciones telefónicas</option>
                        <option value="I07">I07 - Comunicaciones satelitales</option>
                        <option value="I08">I08 - Otra maquinaria y equipo</option>
                        <option value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</option>
                        <option value="D02">D02 - Gastos médicos por incapacidad o discapacidad</option>
                        <option value="D03">D03 - Gastos funerales</option>
                        <option value="D04">D04 - Donativos</option>
                        <option value="D05">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</option>
                        <option value="D06">D06 - Aportaciones voluntarias al SAR</option>
                        <option value="D07">D07 - Primas por seguros de gastos médicos</option>
                        <option value="D08">D08 - Gastos de transportación escolar obligatoria</option>
                        <option value="D09">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</option>
                        <option value="D10">D10 - Pagos por servicios educativos (colegiaturas)</option>
                        <option value="P01">P01 - Por definir</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Información de Contacto */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <Mail className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Información de Contacto</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.contacto.email}
                        onChange={(e) => setFormData({
                          ...formData,
                          contacto: { ...formData.contacto, email: e.target.value }
                        })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="correo@empresa.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                        <Phone className="w-4 h-4 mr-2" />
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        value={formData.contacto.telefono}
                        onChange={(e) => setFormData({
                          ...formData,
                          contacto: { ...formData.contacto, telefono: e.target.value }
                        })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="(55) 1234-5678"
                      />
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    type="button" 
                    className="flex-1 btn-secondary hover-lift flex items-center justify-center"
                    onClick={() => setShowModal(false)}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 btn-primary hover-lift flex items-center justify-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        {selectedClient ? 'Actualizar' : 'Crear'} Cliente
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingClientManagement;